import PageHeader from '../components/layout/PageHeader';
import { useTheme } from '../contexts/ThemeContext';
import {
  useJsonExpandPreference,
  useLayoutModeFromContext,
} from '../contexts/UIPreferencesContext';

/**
 * Preferences (task 625) — the display controls that used to sit in the avatar
 * dropdown between navigation links, given a proper home in the Settings shell.
 *
 * The controls are wired to the exact same context state the avatar menu reads
 * (`ThemeContext` for theme, `UIPreferencesContext` for wide layout and
 * JSON-expand), so the two surfaces stay in lockstep — this page does not
 * remove the dropdown controls (task 626 does that). Preferences are personal
 * and per-browser today (localStorage); each row says so honestly rather than
 * implying account-wide sync.
 */

type ThemePreference = 'light' | 'dark' | 'system';

const THEME_OPTIONS: { value: ThemePreference; label: string }[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

/** Explicit Light / Dark / System segmented control (replaces the blind cycle icon). */
function ThemeSegmentedControl({
  value,
  onChange,
}: {
  value: ThemePreference;
  onChange: (value: ThemePreference) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className="inline-flex rounded-lg border border-gray-300 bg-gray-100 p-0.5 dark:border-gray-600 dark:bg-gray-900/40"
    >
      {THEME_OPTIONS.map((option) => {
        const selected = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            data-testid={`preferences-theme-${option.value}`}
            onClick={() => onChange(option.value)}
            className={`rounded-md px-3 py-1 text-sm font-medium transition-colors ${
              selected
                ? 'bg-white text-indigo-700 shadow-sm dark:bg-indigo-500/25 dark:text-indigo-300'
                : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

/** Accessible on/off toggle, matching the avatar-menu control's look. */
function Toggle({
  checked,
  onChange,
  label,
  testId,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  testId?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
        checked ? 'bg-indigo-500' : 'bg-zinc-300 dark:bg-zinc-600'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
          checked ? 'translate-x-4' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

/** One label/description + control row inside a card. */
function PreferenceRow({
  title,
  description,
  control,
}: {
  title: string;
  description: string;
  control: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-gray-200 py-3 last:border-b-0 dark:border-gray-700">
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">{description}</p>
      </div>
      <div className="flex-shrink-0">{control}</div>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-800">
      <h2 className="text-base font-semibold text-gray-900 dark:text-white">{title}</h2>
      <div className="mt-3">{children}</div>
    </section>
  );
}

export default function PreferencesPage() {
  const { themePreference, setThemePreference } = useTheme();
  const { layoutMode, setLayoutMode } = useLayoutModeFromContext();
  const { jsonExpandedByDefault, toggleJsonExpand } = useJsonExpandPreference();

  return (
    <div>
      <PageHeader
        title="Preferences"
        description="Display settings for this browser. They are saved on this device only."
      />

      <div className="mt-6 flex max-w-2xl flex-col gap-4">
        <Card title="Appearance">
          <PreferenceRow
            title="Theme"
            description="Follow your OS, or pick one explicitly."
            control={
              <ThemeSegmentedControl value={themePreference} onChange={setThemePreference} />
            }
          />
          <PreferenceRow
            title="Wide layout"
            description="Use the full browser width on data-heavy pages."
            control={
              <Toggle
                label="Wide layout"
                testId="preferences-wide-layout"
                checked={layoutMode === 'wide'}
                onChange={(checked) => setLayoutMode(checked ? 'wide' : 'standard')}
              />
            }
          />
        </Card>

        <Card title="Working with data">
          <PreferenceRow
            title="Expand JSON by default"
            description="Open JSON viewers fully expanded in run and response views."
            control={
              <Toggle
                label="Expand JSON by default"
                testId="preferences-json-expand"
                checked={jsonExpandedByDefault}
                onChange={toggleJsonExpand}
              />
            }
          />
        </Card>
      </div>
    </div>
  );
}
